/**
 * ⚠️ ALSO IMPORTED BY THE ASTRO MARKETING BUILD (`marketing/` via the `@app`
 * alias). Keep this module framework-free: **relative imports only** — no `@/`
 * aliases (marketing maps `@/` to its OWN `src/`, so a shared name like
 * `@/lib/utils` would silently resolve to the wrong module), no Next.js,
 * React, or server-only dependencies.
 *
 * Detection + drop helpers for unactionable browser noise in error tracking.
 *
 * Two distinct sources of noise, dropped before ingestion in both PostHog and
 * Sentry:
 *  1. The opaque cross-origin "Script error." (see {@link isOpaqueScriptException}).
 *  2. Errors thrown by code we don't ship — browser extensions, crypto wallets,
 *     and native in-app-browser bridges (iOS WKWebView, Android WebView) that
 *     inject scripts into the page. Each pattern below was adversarially verified
 *     against real PostHog events (every stack frame resolved to an injected /
 *     vendor source, never our bundle) and a codebase grep (zero legitimate
 *     references). They cannot originate from our application.
 *
 * Deliberately NOT included: Safari's generic `"Load failed"`. It rides the same
 * `fetch()` path as real API calls, so suppressing it would hide genuine
 * outages — it stays visible. See the verification verdicts in tasks/.
 *
 * Pure + provider-agnostic so it can be unit-tested without the SDK runtimes.
 */
import { isOpaqueScriptException, type NormalizedException } from './opaque-script-error';

/**
 * Pattern **sources** for third-party injected-script noise — the single source
 * of truth. `id` is a stable label for suppression auditing; `source` is the
 * RegExp body matched against the exception value. Patterns are intentionally
 * tight — each token (e.g. `__firefox__`, `window.ethereum`) cannot appear in a
 * genuine PlayPulse error.
 *
 * Kept as plain strings rather than literals so the marketing site (a separate
 * Astro package whose PostHog snippet is inlined, not bundled) can import them
 * at build time and inject them through `define:vars`, which only carries JSON.
 * See `marketing/src/layouts/BaseLayout.astro`. Adding a pattern here fixes both
 * properties at once — do not maintain a second list.
 */
export const THIRD_PARTY_NOISE_SOURCES: ReadonlyArray<{ id: string; source: string }> = [
  // Firefox-for-iOS reader-mode / content scripts probing their own bridge.
  { id: 'firefox-ios', source: '__firefox__' },
  // Crypto-wallet in-app browsers (MetaMask, Trust) — no Web3 anywhere in app.
  // MUST be revisited before adding any Web3 / window.ethereum usage to our code.
  { id: 'crypto-wallet', source: 'window\\.ethereum' },
  // iOS WKWebView native JS↔native bridge injected by host wrapper apps.
  // MUST be revisited before adding a real WKWebView bridge (window.webkit.messageHandlers) to our code.
  { id: 'ios-webkit-bridge', source: 'window\\.webkit\\.messageHandlers' },
  // Android WebView `@JavascriptInterface` bridge injected by in-app browsers
  // (Instagram/Facebook). Thrown verbatim by the WebView when the host app's Java
  // object has been garbage-collected, so the method name varies with whatever
  // the wrapper injected (`enableDidUserTypeOnKeyboardLogging`, `postMessage`, …).
  // "Java object is gone" is emitted only by Android's WebView bridge and we ship
  // no Android JS bridge, so it can never originate here.
  { id: 'android-webview-bridge', source: 'Error invoking \\S+: Java object is gone' },
  // Outlook SafeLinks / Microsoft MIP SDK COM-interop probing. Anchored on the
  // `:<id>` numeric suffix so it can't match a free-text "object not found" error.
  { id: 'outlook-safelinks', source: 'Object Not Found Matching Id:\\d+' },
];

/** {@link THIRD_PARTY_NOISE_SOURCES} compiled for use in this (bundled) app. */
export const THIRD_PARTY_NOISE: ReadonlyArray<{ id: string; re: RegExp }> =
  THIRD_PARTY_NOISE_SOURCES.map(({ id, source }) => ({ id, re: new RegExp(source) }));

/** True when an exception value matches a known third-party injected-script pattern. */
export function isThirdPartyNoiseValue(value: string | null | undefined): boolean {
  if (!value) return false;
  return THIRD_PARTY_NOISE.some((p) => p.re.test(value));
}

/**
 * True only when EVERY exception in the chain is ignorable noise — either the
 * opaque cross-origin "Script error." or a known third-party injection. A chain
 * that holds even one genuine application error is kept.
 *
 * Note third-party noise keys on the value alone (injected scripts DO carry a
 * useless vendor frame), whereas the opaque-script rule additionally requires
 * zero frames.
 */
export function isIgnorableException(exceptions: NormalizedException[] | undefined): boolean {
  if (!exceptions || exceptions.length === 0) return false;
  return exceptions.every((e) => isOpaqueScriptException(e) || isThirdPartyNoiseValue(e.value));
}

interface PostHogException {
  value?: string | null;
  stacktrace?: { frames?: unknown[] } | null;
}
interface PostHogCaptureResult {
  event?: string;
  properties?: { $exception_list?: PostHogException[] } & Record<string, unknown>;
}

/**
 * PostHog `before_send` handler. Returns `null` to drop ignorable browser-noise
 * `$exception` events; otherwise returns the event unchanged.
 */
export function dropIgnoredBrowserExceptionPostHog<T extends PostHogCaptureResult | null>(
  event: T,
): T | null {
  if (!event || event.event !== '$exception') return event;
  const normalized = (event.properties?.$exception_list ?? []).map((e) => ({
    value: e.value,
    frameCount: e.stacktrace?.frames?.length ?? 0,
  }));
  return isIgnorableException(normalized) ? null : event;
}

interface SentryExceptionValue {
  value?: string | null;
  stacktrace?: { frames?: unknown[] } | null;
}
interface SentryEvent {
  exception?: { values?: SentryExceptionValue[] } | null;
  // Non-exception Sentry events (message captures, transactions) have no
  // `exception` but do carry other fields; modelled so they pass through.
  message?: string;
}

/**
 * Sentry `beforeSend` handler. Returns `null` to drop ignorable browser-noise
 * exception events; otherwise returns the event unchanged.
 */
export function dropIgnoredBrowserExceptionSentry<T extends SentryEvent | null>(
  event: T,
): T | null {
  // Guard null explicitly: null is Sentry's own "drop" sentinel, so we must not
  // manufacture it — we pass the (null) input through, not a drop decision.
  if (!event) return event;
  const values = event.exception?.values;
  if (!values || values.length === 0) return event;
  const normalized = values.map((e) => ({
    value: e.value,
    frameCount: e.stacktrace?.frames?.length ?? 0,
  }));
  return isIgnorableException(normalized) ? null : event;
}
